import { className } from 'lib/css'
import { RefObject, createRef } from 'preact'
import { useEffect, useMemo, useRef, useState } from 'preact/hooks'
import CarouselMessageSlide from 'ui/components/conversation/event/carousel-message/components/slide'
import { useGeneratedId } from 'ui/hooks/utility-hooks'
import CarouselControls from './components/controls'
import CarouselPagination from './components/pagination'

const defaultGetItemKey = (item, idx, prefix) => `${prefix}${idx}`
const defaultGetItemLabel = (item) => item.label
const preventScroll = ({ target }) => {
  target.scrollLeft = 0
}

export default function CarouselComponent({
  currentIndex: originalIndex = 0,
  onChange = () => {},
  getItemKey = defaultGetItemKey,
  getItemLabel = defaultGetItemLabel,
  items,
}) {
  const carouselItemsId = useGeneratedId()
  const itemCount = items.length
  const [currentIndex, setCurrentIndex] = useState(Number(originalIndex || 0))

  useEffect(() => {
    setCurrentIndex(Number(originalIndex || 0))
  }, [setCurrentIndex, originalIndex])

  const slideRefs = useRef<RefObject<any>[]>([])
  slideRefs.current = useMemo(() => {
    return Array(itemCount)
      .fill(1)
      .map(() => createRef())
  }, [itemCount])

  const isMountRef = useRef(true)
  const onChangeRef = useRef<any>(null)
  onChangeRef.current = onChange
  useEffect(() => {
    if (isMountRef.current) {
      isMountRef.current = false
      return
    }

    if (onChangeRef.current) {
      onChangeRef.current(currentIndex)
    }
  }, [currentIndex])

  return (
    <div
      className={className('carousel')}
      role="group"
      aria-roledescription="carousel"
    >
      <div
        className={className('carousel__slides-wrapper')}
        onScroll={preventScroll}
      >
        <div
          id={carouselItemsId}
          className={className('carousel__slides')}
          style={{
            width: `${itemCount * 100}%`,
            left: `${currentIndex * -100}%`,
          }}
          aria-live="polite"
          aria-atomic="false"
        >
          {items.map((item, idx) => {
            const isActive = currentIndex === idx
            return (
              <div
                key={getItemKey(item, idx, 'item-')}
                ref={slideRefs.current[idx]}
                className={className(
                  'carousel__slide',
                  isActive ? 'is-active' : undefined,
                )}
                aria-hidden={!isActive ? 'true' : undefined}
              >
                <CarouselMessageSlide
                  item={item}
                  items={items}
                  index={idx}
                  isActive={isActive}
                />
              </div>
            )
          })}
        </div>
      </div>
      <div className={className('carousel__controls')}>
        <CarouselControls
          currentIndex={currentIndex}
          items={items}
          onChange={setCurrentIndex}
        >
          <div className={className('carousel__pagination')}>
            <CarouselPagination
              currentIndex={currentIndex}
              items={items}
              onChange={setCurrentIndex}
              getItemKey={getItemKey}
              getItemLabel={getItemLabel}
            />
          </div>
        </CarouselControls>
      </div>
    </div>
  )
}
